import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ColumnDef } from '@tanstack/react-table';

export interface AllLocationsTabHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchResultsCount?: number;
  columns: ColumnDef<any>[];
  visibleColumns: Set<string>;
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
}

const AllLocationsTabHeader: React.FC<AllLocationsTabHeaderProps> = ({
  searchQuery,
  onSearchChange,
  searchResultsCount,
  columns,
  visibleColumns,
  onColumnVisibilityChange,
}) => {
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnVisibilityToggle = (columnId: string) => {
    const isVisible = visibleColumns.has(columnId);
    onColumnVisibilityChange(columnId, !isVisible);
  };

  // Get column header name
  const getColumnHeader = (column: ColumnDef<any>): string => {
    if (typeof column.header === 'string') {
      return column.header;
    }
    if ('accessorKey' in column && column.accessorKey) {
      return String(column.accessorKey);
    }
    if (column.id) {
      return String(column.id);
    }
    return 'Unknown';
  };

  return (
    <Box sx={{ 
      p: 2, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2,
      flexShrink: 0,
      borderBottom: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by Location ID or Name..."
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        sx={{ maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      
      {searchQuery && searchResultsCount !== undefined && (
        <Typography sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          Found: {searchResultsCount} locations
        </Typography>
      )}

      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={handleColumnMenuOpen}
        >
          Column Visibility
        </Button>
        <Menu
          anchorEl={columnMenuAnchor}
          open={Boolean(columnMenuAnchor)}
          onClose={handleColumnMenuClose}
          PaperProps={{
            style: {
              maxHeight: 400,
              width: 250,
            },
          }}
        >
          {columns.map((column) => {
            const columnId = (column.id || ('accessorKey' in column ? column.accessorKey : undefined)) as string;
            if (!columnId) return null;
            
            const isVisible = visibleColumns.has(columnId);
            const headerName = getColumnHeader(column);
            
            return (
              <MenuItem 
                key={columnId} 
                onClick={() => handleColumnVisibilityToggle(columnId)}
                dense
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isVisible}
                      onChange={() => handleColumnVisibilityToggle(columnId)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                    />
                  }
                  label={headerName}
                  sx={{ margin: 0, width: '100%' }}
                />
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    </Box>
  );
};

export default AllLocationsTabHeader;
